import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpContext } from '@angular/common/http';
import { ModalController, ToastController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from './storage.service';
import { environment } from 'src/environments/environment';
import { Browser } from '@capacitor/browser';
import {
  Observable, Subject, of, mergeMap, from,
  Subscription, tap, timer,retry, catchError,
  switchMap, map, bindCallback, take,
} from 'rxjs';

import { Device, DeviceId } from '@capacitor/device';

import { IMesura } from './tabs/indicadors/indicadors.page';
import { SpinnerMessageComponent } from './components/spinner-message/spinner-message.component';

import { Ajv, ValidateFunction, JSONSchemaType } from 'ajv';
import formatsPlugin from 'ajv-formats';
import * as model from './api.model';
import { course_status_t, IArt19Meta } from './api.model';
import { ICttMethod } from './tabs/salut/consentiment/consentiment.page';

export const CREDS_KEY = "_auth_";
const ep = (path: string) => `${environment.host}/API/${path}`;

export interface IAuthCredentials {
  access_token: string,
  refresh_token: string
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private _credentials?: IAuthCredentials;
  private _waiting: boolean = false;
  private _sync$ = new Subject<void>();
  private _device_id: DeviceId = { identifier: 'b6:05:34:1c:83:6a' }; // <- s'sinicialitza al constructor!
  private ajv = new Ajv();
  // Dictionary to store validated schemas by key
  private validatedSchemas: { [key: string]: ValidateFunction<any> } = {};
  private _basicAuth = btoa(`${environment.client_id}:${environment.client_secret}`);

  private _session = new Subject<boolean>()
  public session$ = this._session.asObservable();

  // Observable que emet les credencials
  // disponibles en el moment de la subscripció i es completa.
  // En cas de no haver-hi credencials, llença un error custom 420
  private get _authCred$() {
    return new Observable<IAuthCredentials>((observer) => {
      if (!!this._credentials) {
        observer.next(this._credentials);
        observer.complete();
      } else {
        observer.error(new HttpErrorResponse({
          error: "No hi ha credencials",
          status: 420,
          statusText: "Must log-in"
        }));
      }
    });
  }

  // Operador 'pipe' per als Observables que afegeix ui Loading/Toast amb
  // missatges custom, ex: getData().pipe(uify()).subscribe(nxt=>whatever())
  // Si toastError és zero, no es mostren els errors, en cas contrari indica
  // duració màxima en milisegons
  uify<T>(showLoading = true, toastError = 4000) {
    return (observable: Observable<T>) => new Observable<T>(observer => {
      let dialogRef: HTMLIonModalElement;
      let _subs: Subscription;

      // auxiliar que sincronitza l'output de l'observable d'entrada
      // amb el loading i l'observable de sortida
      const _subscribe = () => observable.subscribe({
        next: (value) => observer.next(value),
        error: (err: HttpErrorResponse) => {
          this.zone.run(() => dialogRef?.dismiss(err, 'error'));
          observer.error(err);
          if (!toastError) return;
          const dfltErr = this.translate.instant("login.dfltError"); //"Ha ocurrido un error."
          const previnappMsg = err.headers?.get('x-previnapp-message');
          console.log('DEBUG ERROR message', {previnappMsg, err});
          const msg = Array.isArray(previnappMsg)?previnappMsg[0]:previnappMsg || ''; //!!environment.production?'':err.message;
          this.toastCtl.create({
            message: msg || dfltErr,
            duration: toastError,
            position: 'middle',
            buttons: [this.translate.instant('login.ok')],
          }).then(toast => toast.present())
        },
        complete: () => {
          this.zone.run(() => dialogRef?.dismiss('complete'));
          observer.complete();
        }
      });

      // decideix si cal mostrar el loading
      // NOTA: s'hauria d'evitar mostrar més d'un 'loading' si ja n'hi ha un!
      // i un refcount per saber quan tancar-lo
      if (showLoading) {
        this.modalCtl.create({
          id: '_loading_',
          component: SpinnerMessageComponent,// IonSpinner,
          componentProps: { message: this.translate.instant('login.loadingMessage') },
          cssClass: 'loadingModal',
        }).then(mdl => {
          dialogRef = mdl;
          return dialogRef.present();
        }).then(() => {
          _subs = _subscribe();
          return dialogRef.onDidDismiss();
        }).then(({ data, role }) => {
          //console.log(`DEBUG ${this.constructor.name} loading closed`, {data, role})
        })
      } else _subs = _subscribe();


      // the return value is the teardown function,
      // which will be invoked when the new
      // Observable is unsubscribed from.
      return () => {
        // ens assegurem que el loading es destrueix.
        if (!!dialogRef) {
          this.zone.run(() => dialogRef?.dismiss('destroy'));
        }
        _subs?.unsubscribe();
      }
    });
  }



  backoffRetry<T>({ count, delay } = { count: 5, delay: 1000 }) {
    return (obs$: Observable<T>) => obs$.pipe(
      retry({
        count,
        delay: (_err, retryIndex) => {
          //console.log(`DEBUG ${this.constructor.name} exponential backoff`, _err);
          const d = Math.pow(2, retryIndex - 1) * delay;
          return timer(d);
        }
      })
    );
  }

  // Operador 'pipe' per als Observables que desa en caché el resultat
  // a la clau passada. Es tracta de passar-hi una clau composant
  // el nom del mètode i els paràmetres del mateix en un string
  // TODO: caldria sumar un token a la clau per segmentar per usuari.
  // Cal demanar userID per api!. Altrament, caldria netejar la cache en fer log-out.
  private doCache<T>(key: string) {
    return (observable: Observable<T>) => new Observable<T>(observer => {
      let _subs: Subscription = observable.subscribe({
        next: (value) => {
          try {
            this.storage.setCache(key, value)
          } catch (err) {
            console.log(`ApiService.doCache:`, err);
            this.storage.setCache(key, null);
          } finally {
            observer.next(value)
          }
        },
        error: (err) => observer.error(err),
        complete: () => observer.complete()
      });
      // the return value is the teardown function,
      // which will be invoked when the new
      // Observable is unsubscribed from.
      return () => {
        _subs?.unsubscribe();
      }
    });
  }

  // Utilitat per envoltar un observable i dotar-lo de caché
  public cacheWrap<T>(request: Observable<T>, cacheKey: string, timeout?: number) {
    //*
    return request; // <- Nota: Deshabilito la caché custom
    /*/
    const cachedValue = this.storage.getCache(cacheKey, timeout) as T | undefined;
    return !!cachedValue ? of(cachedValue) : request.pipe(this.doCache(cacheKey));
    //*/
  }

  private validator = <T>(schema: JSONSchemaType<T>, msg: string, key?: string) => map((resp: T) => {

    const schemaKey = key || msg;
    // A producció ens estalviem la validació.
    if (environment.production||true) return resp; //<- a dev també

    // Comprovar si el esquema ja ha estat validado
    let validate: ValidateFunction<T>;
    if (this.validatedSchemas[schemaKey]) {
      validate = this.validatedSchemas[schemaKey] as ValidateFunction<T>;
    } else {
      // Compila el esquema
      validate = this.ajv.compile(schema);
      this.validatedSchemas[schemaKey] = validate;
    }

    if (validate(resp)) return resp;
    else {
      console.error(`Schema validation error for ${msg}:`, validate.errors);
      console.error('Response data:', resp);
      throw new Error(`Error de validació: ${msg}`);
    }

  })

  /**
   * Composa peticions HTTP a partir de les IAuthCredentials llegides en el moment de la susbscripció.
   * Captura els errors 401 (unauthorized) per refrescar les credencials i reintentar la petició amb un nou header.
   */
  private authManager<T>(projfn: (opts: IAuthCredentials) => Observable<T>) {
    // Mostra el LoginComponent i retorna un Observable<any> que es completa
    // en tancar-se el modal, quan les credencials ja s'han refrescat.
    const refreshAuth = () => {
      const { refresh_token } = this._credentials!;
      if (this._waiting) return this._sync$.asObservable();
      this._waiting = true;
      const bdy = new FormData();
      bdy.append('refresh_token', refresh_token);
      return this.http.post<{ access_token: string }>(ep('auth/refresh-token'), bdy, {
        headers: { 'Authorization': `Basic ${this._basicAuth}`}
      }).pipe(
        tap({
          next: (nxt) => {
            //console.log(`DEBUG authManager, refreshAuth responds`, nxt);
            if (!!this._credentials) {
              this._credentials.access_token = nxt.access_token;
              Promise.resolve(this.storage.getCache(CREDS_KEY,0)).then(
                uid=>!!uid?Promise.resolve(uid):this.storage.get(CREDS_KEY)
              ).then(
                (uid:string)=>this.storage.set(`${CREDS_KEY}_${uid}`, this._credentials)
              );
            } else throw new Error('wtf: no credentials?');
            this._sync$.next();
          },
          error: err => {
            console.log('ApiService.authManager', err);
            this._waiting = false;
            delete this._credentials;
            this.storage.remove(CREDS_KEY);
            this.router.navigateByUrl('/');
            this._sync$.next();
          },
          complete: () => { this._waiting = false; }
        })
      );
    }
    // Obté un Observable auxiliar que rellança tots els errors rebuts excepte el 401.
    // Si l'error és el 401, retornarà un observable que indiqui el moment en que hi ha
    // disponibles noves credencials, per poder reintentar la petició.
    const checkAuthDelay = (err: HttpErrorResponse, retryCount: number) => {
      if (err.status == 420) return from(this.router.navigateByUrl('/'));
      if (err.status != 401) throw err;
      return refreshAuth();
    }

    return this._authCred$.pipe(
      mergeMap(projfn),
      retry({ count: 1, delay: checkAuthDelay }), // <- en fallar les credencials amb error 401, espera a que s'hagin renovat i reintenta 1 cop)
      tap({
        // Si desprès de refrescar el token continua fallant amb 401, redirigeix (a login)
        error: (err) => {
          if (err.status == 401) {
            console.log('error en refresc de token', err)
            // cal netejar caché per si es canvia d'usuari
            const uid = this.storage.getCache(CREDS_KEY, 0);
            this.storage.remove(`${CREDS_KEY}_${uid}`);
            this.storage.clearCache();
            this.router.navigateByUrl('/');
          }
        }
      })
    );
  }

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private zone: NgZone,
    private router: Router,
    private modalCtl: ModalController,
    //private loadingCtl: LoadingController,
    private toastCtl: ToastController,
    private translate: TranslateService,
  ) {
    formatsPlugin(this.ajv);
    Device.getId().then(
      (duid) => this._device_id = duid
    ).catch(
      err => console.log(`ApiService getting DeviceId:`, err)
    );
    // Nota: Es poden loguejar N usuaris en N pestanyes diferents del navegador, però
    // només l'últim usuari loguejat podrà recuperar la sessió en fred.
    this.storage.ready.then(
      () => this.storage.get(CREDS_KEY)
    ).then((uid) => {
      this.storage.setCache(CREDS_KEY, uid);
      return this.storage.get(`${CREDS_KEY}_${uid}`);
    }).then((sauth: IAuthCredentials | null) => {
      if (!!sauth) {
        this._credentials = sauth;
        this._session.next(true);
      }
    }).catch(
      (err) => console.log('ApiService failed reading storage', err)
    );
  }


  /** Inicia sessió i refresca les credencials */
  login(user: string, pass?: string, code?: string) {
    const { identifier } = this._device_id;

    const body = new FormData();
    body.append('user', user);
    if (!!pass) body.append('password', btoa(pass));
    if (!!code) body.append('sms_code', code);
    // body.append('device_id', identifier.length < 17 ? identifier.padStart(17, ':') : identifier);
    body.append('device_id', identifier);

    return this.http.post<{ code: string }>(ep('auth/login'), body, {
      headers: { "Authorization": `Basic ${this._basicAuth}` }
    }).pipe(
      switchMap(({ code })=>{
        const pld = new FormData();
        pld.append('code', code);
        return this.http.post<IAuthCredentials>(
          ep('auth/token'), pld, { 
            headers: { "Accept": "application/json", "Authorization": `Basic ${this._basicAuth}` }, 
          }
        );
      }),
      // Mapejo l'error 400 BAD REQUEST amb el missatge d'error retornat per l'endpoint,
      // en la resta d'errades no hi inteferim
      catchError((err: HttpErrorResponse) => {
        //console.log(`DEBUG ${this.constructor.name} caught error:`, err);
        const msg = err.headers?.get('x-previnapp-message') || err.message
        console.log('login caught message', msg, {err});
        if (err.status == 400) throw new Error(msg);
        else throw err;
      }),
      tap({
        next: (creds) => {
          this._credentials = creds;
          this.storage.set(CREDS_KEY, user);
          this.storage.set(`${CREDS_KEY}_${user}`, creds);
          this.storage.setCache(CREDS_KEY, user);
          this._session.next(true);
        }
      }),
    )
  }

  loginSMS(user: string) {
    const body = new FormData();
    body.append('user', user);
    return this.http.post<void>(
      ep('auth/login-sms'), body, {
      headers: { "Authorization": `Basic ${this._basicAuth}` }
    });
  }

  // callback https://app.previntegral.com/acces_sms/?user=DNI
  loginGoogle() {
    // requereix configurar el deep-linking per l'app:
    // https://capacitorjs.com/docs/guides/deep-links
    const { identifier } = this._device_id;
    const params: any = {
      client_id: environment.client_id,
      client_secret: environment.client_secret,
      device_id: identifier.length < 17 ? identifier.padStart(17, ':') : identifier,
      redirect_uri: `${environment.webhost}/callback`
    }
    // return this.http.get(ep('auth/login-google'), { params, responseType: "text" });
    const url = `${ep('auth/login-google')}?client_id=${encodeURIComponent(params.client_id)
      }&client_secret=${encodeURIComponent(params.client_secret)
      }&device_id=${encodeURIComponent(params.device_id)
      }&redirect_uri=${encodeURIComponent(params.redirect_uri)
      }`;
    // Notes: A la webApp fem servir la mateixa pestanya (el redirect final ens tornarà a l'app),
    // per les versions natives, que ignoren 'windowName', potser cal afegir listeners al Browser
    Browser.open({ url, windowName: '_self' }).catch(err => console.log('loginGoogle, Browser error', err));
  }
  remataLoginGoogle({ code }: { code: string }) {
    const body = new FormData();
    body.append('code', code);
    return this.http.post<IAuthCredentials>(
      ep('auth/token'), body, {
        headers: { "Accept": "application/json", "Authorization": `Basic ${this._basicAuth}` },
      }
    ).pipe(
      tap({
        next: (creds) => {
          this._credentials = creds;
          this.storage.set(CREDS_KEY, code);
          this.storage.set(`${CREDS_KEY}_${code}`, creds);
          this.storage.setCache(CREDS_KEY, code);
          this._session.next(true);
        }
      }),
    );
  }

  logout() {
    const cleanup = ()=>{
      console.log("cleaning caché & credentials")
      const uid = this.storage.getCache(CREDS_KEY, 0);
      this.storage.remove(`${CREDS_KEY}_${uid}`);
      this.storage.clearCache();
      this._session.next(false);
    }
    return this.authManager(({ access_token }) => this.http.post<void>(
      ep('auth/logout'), null, { headers: { "Authorization": `Bearer ${access_token}` } }
    )).pipe(tap({
      error: (err)=>{console.log('logout error', err); cleanup() },
      complete: cleanup
    }));
  }

  restorePassword(user: string) {
    const body = new FormData();
    body.append('user', user);
    return this.http.post<void>(
      ep('auth/restore-password'), body, {
      headers: { "Authorization": `Basic ${this._basicAuth}` }
    }
    ).pipe(this.uify())
  }

  // retorna la petició de llista les qualificacions de risc disponibles
  // private _riscsAvailable() {
  public riscsAvailable() {
    //const ctx = new HttpContext().set(CACHING_K, 'risksAvailable');

    //console.log('debug httpcontext', ctx );
    return this.authManager(({ access_token }) => this.http.get<model.AryRiscsAvailable>(
      ep('v1/APPPT/RISK_QUALIFICATION/GET_RISK_QUALIFICATION'),
      { params: { access_token: access_token }, 
        headers: { '_cache_key_': 'risksAvailable'}
       //context: ctx //new HttpContext().set(CACHING_K, 'risksAvailable')
      }
    )).pipe(
      this.validator(model.schemaAryRiscsAvailable, "GET_RISK_QUALIFICATION"),
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[b.risk_qualification_id] = b.risk_qualification_nom; return a },
        {} as { [k: string]: string }
      ) : {})
    );
  }
  // // observa els valors cachejats, o retorna la petició i en cacheja el resultat
  // public riscsAvailable() {
  //   return this.cacheWrap(this._riscsAvailable(), 'risksAvailable')
  // }

  // llista els origens dels riscos
  // private _riscSources() {
  public riscSources() {
    return this.authManager(({ access_token }) => this.http.get<model.AryRiscSources>(
      ep('v1/APPPT/RISK_SOURCES/GET_RISK_SOURCES'),
      { params: { access_token }, headers: { '_cache_key_': 'riscSources'} }
    )).pipe(
      this.validator(model.schemaAryRiscSources, 'GET_RISK_SOURCES'),
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[b.risk_source_type_id] = b.risk_source_name; return a },
        {} as { [k: string]: string }
      ) : {})
    )
  }
  // public riscSources() {
  //   return this.cacheWrap(this._riscSources(), 'riscSources');
  // }

  // llista els tipus de mesures
  //private _mesuresTypes() {
  public mesuresTypes() {
    return this.authManager(({ access_token }) => this.http.get<model.AryMesuresTypes>(
      ep('v1/APPPT/MEASURES/GET_MEASURE_TYPES'),
      { params: { access_token }, headers: { '_cache_key_': 'mesuresTypes'} }
    )).pipe(
      this.validator(model.schemaAryMesuresTypes, 'GET_MEASURE_TYPES'),
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[b.measure_type_id] = b.measure_type_name; return a },
        {} as { [k: string]: string }
      ) : {})
    )
  }
  // public mesuresTypes() {
  //   return this.cacheWrap(this._mesuresTypes(), 'mesuresTypes')
  // }

  // llista els possibles estats de les mesures
  // private _estatsMesures() {
  public estatsMesures() {
    return this.authManager(({ access_token }) => this.http.get<model.AryEstatsMesures>(
      ep('v1/APP/ACTIONS_STATUS/GET_AVAILABLE_MEASURES_STATUS'),
      { params: { access_token }, headers: { '_cache_key_': 'estatsMesures'} }
    )).pipe(
      this.validator(model.schemaAryEstatsMesures, 'GET_AVAILABLE_MEASURES_STATUS'),
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[`${b.status_id}`] = b.status_name; return a },
        {} as { [k: string]: string }
      ) : {})
    );
  }
  // public estatsMesures() {
  //   return this.cacheWrap(this._estatsMesures(), 'estatsMesures');
  // }

  // private _contacteTecnics(company_id: string) {
  public contacteTecnics(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryContacteTecnics>(
      ep('v1/APPAD/COMPANY_MANAGERS/GET_PREVENTION_TECHNICIAN_CONTACT_METHODS'),
      { params: { access_token, company_id }, headers: { '_cache_key_': `contacteTecnics_${company_id}`} }
    )).pipe(
      this.validator(model.schemaContacteTecnics, "GET_PREVENTION_TECHNICIAN_CONTACT_METHODS")
    );
  }
  // public contacteTecnics(company_id: string) {
  //   return this.cacheWrap(this._contacteTecnics(company_id), `contacteTecnics_${company_id}`);
  // }

  // obté la informació de l'usuari (nom i cognoms)
  //private _userInfo() {
  public userInfo() {
    return this.authManager(({ access_token }) => this.http.get<model.AryUserInfo>(
      ep('v1/APP/USERS/GET_USER_INFO'),
      { params: { access_token }, headers: { '_cache_key_': `userInfo`} }
    )).pipe(
      this.validator(model.schemaAryUserInfo, 'GET_USER_INFO'),
      map((nxt) => nxt[0]),
    );
  }
  // public userInfo() {
  //   return this.cacheWrap(this._userInfo(), 'userInfo');
  // }

  // obté les empreses de l'usuari
  // private _userCompanies() {
  public userCompanies() {
    return this.authManager(({ access_token }) => this.http.get<model.AryUserCompanies>(
      ep('v1/APPAD/COMPANIES/GET_USER_COMPANIES'),
      { params: { access_token }, headers: { '_cache_key_': `userCompanies`} }
    )).pipe(
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      this.validator(model.schemaAryUserCompanies, 'GET_USER_COMPANIES'),
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[`${b.company_id}`] = b.company_name; return a },
        {} as { [k: string]: string }
      ) : {}),
      // catchError(error => {
      //   console.error('Error in userCompanies:', error);
      //   // Return an empty object instead of failing
      //   return of({} as { [k: string]: string });
      // })
    );
  }
  // public userCompanies() {
  //   return this.cacheWrap(this._userCompanies(), 'userCompanies');
  // }

  // Comprova si l'usuari té accés a la vista 'gestió' d'una companyia
  //private _isPRL(company_id: string) {
  public isPRL(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<[{ isPRL: boolean }]>(
      ep('v1/APPAD/COMPANIES/IS_PRL'),
      { params: { access_token, company_id }, headers: { '_cache_key_': `isPRL_${company_id}`} }
    ))
  }
  // public isPRL(company_id: string) {
  //   return this.cacheWrap(this._isPRL(company_id), `isPRL_${company_id}`);
  // }

  public isFAC(company_id: string) {
    return this.authManager(({ access_token })=>this.http.get<{isFAC:boolean}[]>(
      ep('v1/APPAD/COMPANIES/IS_FAC'), {
        params: { access_token, company_id }, headers: { '_cache_key_': `isFAC_${company_id}`}
      }
    ))
  }

  workPlaces(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryWorkPlaces>(
      ep('v1/APPAD/WORKPLACES/GET_WORKPLACES'),
      { params: { access_token, company_id }, headers: { '_cache_key_': `workPlaces_${company_id}`} }
    )).pipe(
      this.validator(model.schemaAryWorkPlaces, 'GET_WORKPLACES'),
      // converteix els arrais en diccionaris
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => {
          a[`${b.workplace_id}`] = {
            name: b.workplace_name,
            sections: Array.isArray(b.sections) ? b.sections.reduce(
              (c, d) => { c[`${d.section_id}`] = d.section_name; return c },
              {} as { [k: string]: string }
            ) : {}
          };
          return a
        }, {} as { [workplace_id: string]: { name: string, sections: { [section_id: string]: string } } }
      ) : {})
    );
  }
  // public workPlaces(company_id: string) {
  //   return this.cacheWrap(this._workPlaces(company_id), `workPlaces_${company_id}`);
  // }

  companyContracts(company_id: string) {
    return this.authManager(({ access_token }) =>
      this.http.get<Array<{
        contract_id: number;
        contract_name: string;
        technical_prevention: boolean;
        healthcare: boolean;
        training: boolean;
        others: boolean;
        renewals: Array<{
          company_contract_id: number;
          status_id: number;
          start_date: string;
          end_date: string;
        }>;
      }>>(
        ep('v1/APPAD/COMPANY_CONTRACTS/GET_COMPANY_CONTRACTS'),
        { params: { access_token, company_id }, headers: { '_cache_key_': `companyContracts_${company_id}`} }
      )
    );
  }

  // public companyContracts(company_id: string) {
  //   return this.cacheWrap(this._companyContracts(company_id), `companyContracts_${company_id}`);
  // }

  assessment(workplace_id: string, all = false) {
    return this.authManager(({ access_token }) => this.http.get<model.AryAssessment>(
      ep('v1/APPPT/ASSESSMENTS/GET_ASSESSMENT'),
      { params: { access_token, workplace_id, all }, headers: { '_cache_key_': `assessment_${workplace_id}_${all}`} }
    )).pipe(
      this.validator(model.schemaAryAssessment, 'GET_ASSESSMENT')
    );
  }
  // public assessment(workplace_id: string, all = false) {
  //   return this.cacheWrap(this._assessment(workplace_id, all), `assessment_${workplace_id}_${all}`);
  // }

  risks(assessment_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.AryRisks>(
      ep('v1/APPAD/COMPANY_RISKS/GET_ASSESSMENT_RISKS'),
      { params: { access_token, assessment_id }, headers: { '_cache_key_': `risks_${assessment_id}`} }
    )).pipe(
      this.validator(model.schemaAryRisks, 'GET_ASSESSMENT_RISKS')
    );
  }
  // public risks(assessment_id: number) {
  //   return this.cacheWrap(this._risks(assessment_id), `risks_${assessment_id}`);
  // }

  mesures(assessment_id: number, risk_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.AryMeasures>(
      ep('v1/APPAD/COMPANY_RISKS_MEASURES/GET_MEASURES'),
      { params: { access_token, assessment_id, risk_id }, headers: { '_cache_key_': `mesures_${assessment_id}_${risk_id}`} }
    )).pipe(
      this.validator(model.schemaAryMeasures, 'GET_MEASURES')
    );
  }
  // public mesures(assessment_id: number, risk_id: number) {
  //   return this.cacheWrap(this._mesures(assessment_id, risk_id), `mesures_${assessment_id}_${risk_id}`);
  // }

  mesuresh(assessment_id: number, company_risk_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.AryMeasures>(
      ep('v1/APPAD/COMPANY_RISKS_MEASURES/GET_MEASURES'),
      { params: { access_token, assessment_id, company_risk_id }, headers: {
        '_cache_key_': `mesures_${assessment_id}_${company_risk_id}`}
      }
    )).pipe(
      this.validator(model.schemaAryMeasures, 'GET_MEASURES')
    );
  }

  assessmentMeasures(assessment_id: number, company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryAssessmentMeasures>(
      ep('v1/APPAD/COMPANY_RISKS_MEASURES/GET_ASSESSMENT_MEASURES'),
      { params: { access_token, assessment_id, company_id }, headers: { '_cache_key_': `assessmentMeasures_${assessment_id}_${company_id}`} }
    )).pipe(
      this.validator(model.schemaAryAssessmentMeasures, 'GET_ASSESSMENT_MEASURES')
    );
  }

  histMesures(assessment_id: number, company_risk_id: number, measure_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.AryHistMeasures>(
      ep('v1/APPAD/COMPANY_RISKS_MEASURES_STATUS/GET_HIST_STATUS_MEASURE'), {
      params: { access_token, assessment_id, company_risk_id, measure_id }, 
      headers: { '_cache_key_': `histMesures_${assessment_id}_${company_risk_id}_${measure_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryHistMeasures, 'GET_HIST_STATUS_MEASURE'),
      // map(hist=>hist.map(item=>{
      //   // canvia el format de les dates dd-mm-aaaa per aaaa-mm-dd
      //   if (!!item.date) item.date = item.date.replace(/(\d{2})-(\d{2})-(\d{4})/, '$3-$2-$1')
      //   return item;
      // }))
    );
  }
  // public histMesures(assessment_id: number, company_risk_id: number, measure_id: number) {
  //   return this.cacheWrap(
  //     this._histMesures(assessment_id, company_risk_id, measure_id),
  //     `histMesures_${assessment_id}_${company_risk_id}_${measure_id}`);
  // }

  histStatusDocument(assessment_id: number, company_risk_id: number, measure_id: number, document_id: number) {
    return this.authManager(({access_token})=>this.http.get<model.FileBlobResponse[]>(
      ep('v1/APPAD/COMPANY_RISKS_MEASURES_STATUS/GET_MEASURE_DOCUMENT'), {
        params: { access_token, assessment_id, company_risk_id, measure_id, document_id }
      }
    ));
  }

  riscSourceName(assessment_id: string|number, risk_source_type_id: number, risk_source_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryRiscSourceName>(
      ep('v1/APPPT/RISK_SOURCES/GET_RISK_SOURCE_NAME'), {
      params: { access_token, assessment_id, risk_source_type_id, risk_source_id }, 
      headers: { '_cache_key_': `riscSourceName_${assessment_id}_${risk_source_type_id}_${risk_source_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryRiscSourceName, 'GET_RISK_SOURCE_NAME')
    )
  }
  // public riscSourceName(company_id: string, risk_source_type_id: number, risk_source_id: string) {
  //   return this.cacheWrap(
  //     this._riscSourceName(company_id, risk_source_type_id, risk_source_id),
  //     `riscSourceName_${company_id}_${risk_source_type_id}_${risk_source_id}`
  //   )
  // }

  mesuraDescription(assessment_id: number, risk_id: number, measure_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.AryMeasureDescription>(
      ep('v1/APPAD/COMPANY_RISKS_MEASURES/GET_MEASURES_LONG_DESCRIPTION'),
      { params: { access_token, assessment_id, risk_id, measure_id } }
    )).pipe(
      this.validator(model.schemaAryMeasureDescription, 'GET_MEASURES_LONG_DESCRIPTION')
    );
  }

  // Nota: d'aquesta crida no en faig caché.
  assessmentDocument(assessment_id: number, opts:{
    section_id?:number, company_job_id?:number, company_machine_id?:number, company_chemical_id?:number
  } = {}) {
    return this.authManager(({ access_token }) => this.http.get<model.AssessmentDocument>(
      ep('v1/APPPT/ASSESSMENTS/GET_ASSESSMENT_DOCUMENT'), {
      params: { access_token, assessment_id, ...opts }
    }
    )).pipe(
      this.validator(model.schemaAssessmentDocument, 'GET_ASSESSMENT_DOCUMENT')
    );
  }

  public assessmentDocumentParams(
    assessment_id: number,
    opts: { section_id?: string, company_job_id?: string, company_machine_id?: string, company_chemical_id?: string }
  ) {
    return this.authManager(({ access_token }) => this.http.get<model.AssessmentDocument>(
      ep('v1/APPPT/ASSESSMENTS/GET_ASSESSMENT_DOCUMENT'), {
      params: {
        access_token,
        assessment_id,
        ...Object.fromEntries(Object.entries(opts).filter(([_, v]) => !!v))
      }
    }
    )).pipe(
      this.validator(model.schemaAssessmentDocument, 'GET_ASSESSMENT_DOCUMENT')
    );
  }

  assessmentSources(assessment_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.AryAssessmentSources>(
      ep('v1/APPPT/ASSESSMENTS/GET_ASSESSMENT_SOURCES'), {
      params: { access_token, assessment_id }, headers: { '_cache_key_': `assessmentSources_${assessment_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryAssessmentSources, 'GET_ASSESSMENT_SOURCES')
    );
  }
  // public assessmentSources(assessment_id: number) {
  //   return this.cacheWrap(this._assessmentSources(assessment_id), `assessmentSources_${assessment_id}`);
  // }

  bills(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryBills>(
      ep('v1/APPAD/BILLS/GET_BILLS'),
      { params: { access_token, company_id }, headers: { '_cache_key_': `bills_${company_id}`} }
    )).pipe(
      this.validator(model.schemaAryBills, 'GET_BILLS')
    );
  }
  // public bills(company_id: string) {
  //   const cacheKey = `bills_${company_id}`;
  //   return this.cacheWrap(this._bills(company_id), cacheKey)
  // }

  billDocument(bill_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.BillDocument>(
      ep('v1/APPAD/BILLS/GET_BILL_DOCUMENT'),
      { params: { access_token, bill_id } }
    )).pipe(
      this.validator(model.schemaBillDocument, 'GET_BILL_DOCUMENT')
    );
  }

  languages() {
    return this.authManager(({ access_token }) => this.http.get<model.AryLanguages>(
      ep('v1/APP/LANGUAGES/GET_LANGUAGES'),
      { params: { access_token }, headers: { '_cache_key_': `languages`} }
    )).pipe(
      this.validator(model.schemaAryLanguages, 'GET_LANGUAGES'),
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[`${b.language_id}`] = b.language_name; return a },
        {} as { [k: string]: string }
      ) : {})
    );
  }
  // public languages() {
  //   return this.cacheWrap(this._languages(), 'languages');
  // }

  userLanguage() {
    return this.authManager(({ access_token }) => this.http.get<{ language_id: string }>(
      ep('v1/APP/USERS/GET_USER_LANGUAGE'),
      { params: { access_token }, headers: { '_cache_key_': `userLanguage`} }
    ));
  }
  // public userLanguage() {
  //   return this.cacheWrap(this._userLanguage(), 'userLanguage');
  // }

  updateUserLanguage(language_id: string) {
    return this.authManager(({ access_token }) => this.http.post<{}>(
      ep('v1/APP/USERS/POST_UPDATE_USER_LANGUAGE'), {},
      { headers: { 'Content-Type': 'application/json' }, params: { access_token, language_id } }
    ));
  }

  postMeasureStatus(assessment_id: number, mesura: IMesura, status: {
    status_id: number,
    responsible: string,
    date: string,
    commment: string,
    budget: number,
    adjunts: {
      id: string, blob: Blob,
      filename: string, description: string,
      show: boolean
    }[]
  }) {

    const fdata = new FormData();
    status.adjunts.forEach(adjunt => {
      const { id, filename, description, show, blob } = adjunt;
      const _desc = JSON.stringify({ filename, description });
      const _show = JSON.stringify({ filename, show });
      fdata.append("attachments[]", blob, id);
      fdata.append("attachments_descriptions[]", _desc);
      fdata.append("attachments_show[]", _show);
    });

    const params: any = { assessment_id, ...mesura, ...status };

    delete params.measure_name;
    delete params.measure_description;
    delete params.type
    delete params.adjunts

    //params.date = params.date.replace(/(\d{4})-(\d{2})-(\d{2})/, '$3/$2/$1')

    return this.authManager(({ access_token }) => this.http.post(
      ep('v1/APPAD/COMPANY_RISKS_MEASURES_STATUS/POST_MEASURE_STATUS'), fdata, {
      params: { access_token, ...params }, responseType: 'text'
    }
    )).pipe(this.uify())
  }

  documentUploadRequirements() {
    return this.authManager(({ access_token }) => this.http.get<model.DocumentUploadRequirements>(
      ep('v1/APPCOMU/DOCUMENTS/GET_DOCUMENT_REQUIREMENTS'), {
      params: { access_token }, headers: { '_cache_key_': `documentUploadRequirements`}
    }
    )).pipe(
      this.validator(model.schemaDocumentUploadRequirements, 'GET_DOCUMENT_REQUIREMENTS')
    );
  }
  // public documentUploadRequirements() {
  //   return this.cacheWrap(this._documentUploadRequirements(), 'documentUploadRequirements').pipe(
  //     this.uify()
  //   );
  // }
  prlDocuments() {
    return this.authManager(({ access_token }) => this.http.get<model.AryPrlDocuments>(
      ep('v1/APPPT/PRL_DOCUMENTS/GET_PRL_DOCUMENTS'), {
      params: { access_token }, headers: { '_cache_key_': `prlDocuments`}
    }
    )).pipe(
      this.validator(model.schemaAryPrlDocuments, 'GET_PRL_DOCUMENTS')
    )
  }
  // public prlDocuments() {
  //   return this.cacheWrap(this._prlDocuments(), `prlDocuments`);
  // }
  // nota: no fem caché als downloads
  public prlDocumentDownload(prl_document_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.PrlDocumentDownload>(
      ep('v1/APPPT/PRL_DOCUMENTS/GET_PRL_DOCUMENTS_BLOB'),
      { params: { access_token, prl_document_id } }
    )).pipe(
      this.validator(model.schemaPrlDocumentDownload, 'GET_PRL_DOCUMENTS_BLOB')
    )
  }

  contractDocs(company_contract_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.AryContractDocuments>(
      ep('v1/APPAD/COMPANY_CONTRACTS/GET_COMPANY_CONTRACT_DOCUMENTS'), {
      params: { access_token, company_contract_id }, headers: { '_cache_key_': `contractDocs_${company_contract_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryContractDocuments, 'GET_COMPANY_CONTRACT_DOCUMENTS')
    );
  }

  // public contractDocs(company_contract_id: number) {
  //   return this.cacheWrap(this._contractDocs(company_contract_id), `contractDocs_${company_contract_id}`);
  // }

  public contractDocDownload(company_contract_id: number, contract_document_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.FileBlobResponse>(
      ep('v1/APPAD/COMPANY_CONTRACTS/GET_COMPANY_CONTRACT_DOCUMENT'), {
      params: { access_token, company_contract_id, contract_document_id }
    }
    )).pipe(
      this.validator(model.schemaFileBlobResponse, 'GET_COMPANY_CONTRACT_DOCUMENT')
    );
  }

  companyDocuments(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryCompanyDocuments>(
      ep('v1/APPAD/COMPANY_DOCUMENTS/GET_COMPANY_DOCUMENTS'), {
      params: { access_token, company_id }, headers: { '_cache_key_': `companyDocuments_${company_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryCompanyDocuments, 'GET_COMPANY_DOCUMENTS'),
    );
  }
  // public companyDocuments(company_id: string) {
  //   return this.cacheWrap(this._companyDocuments(company_id), `companyDocuments_${company_id}`);
  // }
  public companyDocumentDownload(company_id: string, document_id: number, archived:boolean=false) {
    return this.authManager(({ access_token }) => this.http.get<model.FileBlobResponse[]>(
      ep('v1/APPAD/COMPANY_DOCUMENTS/GET_COMPANY_DOCUMENT'), {
      params: { access_token, company_id, document_id, archived }
    }
    )).pipe(
      map(([resp]) => resp),
      this.validator(model.schemaFileBlobResponse, 'GET_COMPANY_DOCUMENT'),
    )
  }

  uploadDocument(company_id: string, document: {
    author: string,
    category: string,
    date: string,
    name: string,
    file: File,
  }) {
    const fdata = new FormData();
    fdata.append("attachment", document.file, document.name);
    fdata.append("date", document.date);
    fdata.append("category", document.category);
    fdata.append("author", document.author);
    return this.authManager(({ access_token }) => this.http.post<{ document_id: number }>(
      ep('v1/APPAD/COMPANY_DOCUMENTS/POST_COMPANY_DOCUMENT'), fdata, {
      params: { access_token, company_id }
    }
    )).pipe(this.uify());
  }

  companyWorkers(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryCompanyWorkers>(
      ep('v1/APPAD/COMPANY_WORKERS/GET_WORKERS'), {
      params: { access_token, company_id }, headers: { '_cache_key_': `companyWorkers_${company_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryCompanyWorkers, 'GET_WORKERS'),
    )
  }
  // public companyWorkers(company_id: string) {
  //   return this.cacheWrap(this._companyWorkers(company_id), `companyWorkers_${company_id}`);
  // }

  companyJobs(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryCompanyJobs>(
      ep('v1/APPAD/COMPANY_JOBS/GET_COMPANY_JOBS'), {
      params: { access_token, company_id }, headers: { '_cache_key_': `companyJobs_${company_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryCompanyJobs, 'GET_COMPANY_JOBS'),
      map(e => e.reduce((a, b) => {
        a[b.company_job_id] = b.company_job_name;
        return a;
      }, {} as { [k: string]: string }))
    )
  }
  // public companyJobs(company_id: string) {
  //   return this.cacheWrap(this._companyJobs(company_id), `companyJobs_${company_id}`);
  // }

  // NOTA DEGUT AL CANVI DE FORMAT caldrà fer la validació en varios passos, convertint objectes en arrais.
  companyCourses(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<{ [contact_id: string]: any }>(
      ep('v1/APPFO/COURSES/GET_PRL_COURSES'), {
      params: { access_token, company_id }, headers: { '_cache_key_': `companyCourses_${company_id}`}
    }
    )).pipe(
      // Valido que sigui un objecte amb propietats (primer pass)
      this.validator(model.schemaAnyObj,
        'GET_PRL_COURSES', 'prl_courses_pass1'
      ),
      map(nxt => {
        // Nota: transformo l'objecte retornat per que sigui validable (segon pas)
        const values: model.IPRLCourse[] = []
        Object.keys(nxt).forEach((key: string) => {
          const val: model.IPRLCourse = {
            contact_id: key,
            agreements: Object.values(nxt[key])
          };
          values.push(val)
        });
        return values;
      }),
      // segon pas de validació
      this.validator(model.schemaIPRLCourses, 'GET_PRL_COURSES', 'prl_courses_pass2')
    )
  }

  // public companyCourses(company_id: string) {
  //   return this.cacheWrap(this._companyCourses(company_id), `companyCourses_${company_id}`);
  // }

  // NOTA DEGUT AL CANVI DE FORMAT caldrà fer la validació en varios passos, convertint objectes en arrais.
  workerCourses(company_id: string, contact_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryWorkerCourses>(
      ep('v1/APPFO/COURSES/GET_WORKER_PRL_COURSES'), {
      params: { access_token, company_id, contact_id }, 
      headers: { '_cache_key_': `workerCourse_${company_id}_${contact_id}`}
    }
    )).pipe(
      this.validator(model.schemaAnyObj,
        'GET_WORKER_PRL_COURSES', 'getWorkerPLRCourses_pas1'
      ),
      map(nxt => {
        // Nota: transformo l'objecte retornat per que sigui validable (segon pas)
        const values: model.IPRLCourse[] = []
        Object.keys(nxt).forEach((key: string) => {
          const val: model.IPRLCourse = {
            contact_id: key,
            agreements: Object.values(nxt[key])
          };
          values.push(val)
        });
        return values;
      }),
      // segon pas de validació
      this.validator(model.schemaIPRLCourses, 'GET_WORKER_PRL_COURSES', 'worker_prl_courses_pass2')
    )
  }

  // public workerCourses(company_id: string, contact_id: string) {
  //   return this.cacheWrap(
  //     this._workerCourses(company_id, contact_id),
  //     `workerCourses_${company_id}_${contact_id}`
  //   );
  // }

  coursesStatus() {
    return this.authManager(({ access_token }) => this.http.get<Record<course_status_t, IArt19Meta>>(
      ep('v1/APPFO/COURSES/GET_PRL_COURSES_STATUS'), {
      params: { access_token }, headers: { '_cache_key_': `coursesStatus`}
    }
    )).pipe(
      this.validator(model.schemaIPRLCourseStatus, 'GET_PRL_COURSES_STATUS'),
    );
  }
  // public coursesStatus() {
  //   return this.cacheWrap(this._coursesStatus(), `coursesStatus`);
  // }

  formationCertificate(id_courses: number, company_worker_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.FileBlobResponse>(
      ep('v1/APPFO/RECEIVED_COURSES/GET_FORMATION_CERTIFICATE'), {
      params: { access_token, id_courses, company_worker_id },
      headers: { '_cache_key_': `formationCertificates_${id_courses}_${company_worker_id}`}
    }
    )).pipe(
      this.validator(model.schemaFileBlobResponse, 'GET_FORMATION_CERTIFICATE'),
    );
  }
  // public formationCertificate(id_courses: number, compnay_worker_id: string) {
  //   return this.cacheWrap(
  //     this._formationCerfificate(id_courses, compnay_worker_id),
  //     `formationCertificates_${id_courses}_${compnay_worker_id}`
  //   );
  // }

  workerMachines(company_id: string, contact_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryWorkerMachines>(
      ep('v1/APPAD/COMPANY_MACHINES/GET_WORKER_MACHINES'), {
      params: { access_token, company_id, contact_id }, 
      headers: { '_cache_key_': `workerMachines_${company_id}_${contact_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryWorkerMachines, 'GET_WORKER_MACHINES'),
    );
  }
  // public workerMachines(company_id: string, contact_id: string) {
  //   return this.cacheWrap(this._workerMachines(company_id, contact_id),
  //     `workerMachines_${company_id}_${contact_id}`);
  // }

  workerChemicals(company_id: string, contact_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryWorkerChemicals>(
      ep('v1/APPAD/COMPANY_CHEMICALS/GET_WORKER_CHEMICALS'), {
      params: { access_token, company_id, contact_id }, 
      headers: { '_cache_key_': `workerChemicals_${company_id}_${contact_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryWorkerChemicals, 'GET_WORKER_CHEMICALS'),
    );
  }
  // public workerChemicals(company_id: string, contact_id: string) {
  //   return this.cacheWrap(this._workerChemicals(company_id, contact_id),
  //     `workerChemicals_${company_id}_${contact_id}`);
  // }

  checkups(company_id: string, pending:boolean=true) {
    return this.authManager(({ access_token }) => this.http.get<model.AryCheckups>(
      ep('v1/APPVS/CHECKUPS/GET_CHECKUPS'), {
      params: { access_token, company_id, pending }, headers: { '_cache_key_': `checkups_${company_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryCheckups, 'GET_CHECKUPS'),
    );
  }
  // public checkups(company_id: string) {
  //   return this.cacheWrap(this._checkups(company_id), `checkups_${company_id}`);
  // }

  checkupDocument(checkup_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.FileBlobResponse>(
      ep('v1/APPVS/CHECKUPS/GET_CHECKUP_DOCUMENT'), {
      params: { access_token, checkup_id }, // headers: { '_cache_key_': `checkupDocument_${checkup_id}`}
    }
    )).pipe(
      this.validator(model.schemaFileBlobResponse, 'GET_CHECKUP_DOCUMENT')
    );
  }
  // public checkupDocument(checkup_id: number) {
  //   return this.cacheWrap(this._checkupDocument(checkup_id), `checkupDocument_${checkup_id}`);
  // }

  downloadEmployeesXSL(company_id:string) {
    return this.authManager(({ access_token })=>this.http.get<model.FileBlobResponse>(
      ep('v1/APPAD/COMPANY_WORKERS/GET_WORKERS_XLSX'), {
        params: { access_token, company_id }
      }
    )).pipe(
      this.validator(model.schemaFileBlobResponse, 'GET_WORKERS_XLSX')
    );
  }

  getVisitReports(company_id: string) {
    return this.authManager(({access_token})=>this.http.get<model.AryVisitReports>(
      ep('v1/APPAD/VISIT_REPORTS/GET_VISIT_REPORTS'), {
        params: { access_token, company_id }, headers: { '_cache_key_': `visitReports_${company_id}`}
      }
    )).pipe(
      this.validator(model.schemaAryVisitReports, 'GET_VISIT_REPORTS')
    )
  }
  // public getVisitReports(company_id: string) {
  //   return this.cacheWrap(this._getVisitReports(company_id), `visitReports_${company_id}`)
  // }
  
  public getVisitReportsDocument(company_id: string, visit_report_id: number) {
    return this.authManager(({access_token})=>this.http.get<model.FileBlobResponse>(
      ep('v1/APPAD/VISIT_REPORTS/GET_VISIT_REPORT_DOCUMENT'), {
        params: { access_token, company_id, visit_report_id }
      }
    ));
  }

  checkupStatus() {
    return this.authManager(({ access_token }) => this.http.get<model.AryCheckupStatus>(
      ep('v1/APPVS/CHECKUPS/GET_CHECKUP_STATUS'), {
      params: { access_token }, headers: { '_cache_key_': `checkupStatus`}
    }
    )).pipe(
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      this.validator(model.schemaAryCheckupStatus, 'GET_CHECKUP_STATUS'),
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[`${b.status_id}`] = b.status_name; return a },
        {} as { [k: string]: string }
      ) : {})
    );
  }
  // public checkupStatus() {
  //   return this.cacheWrap(this._checkUpStatus(), 'checkUpStatus');
  // }

  checkupQuestions(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryCheckupQuestions>(
      ep('v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS'), {
      params: { access_token, checkup_id }, headers: { '_cache_key_': `checkupQuestions_${checkup_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryCheckupQuestions, 'GET_CHECKUP_QUESTIONS'),
    )
  }

  // public checkupQuestions(checkup_id: string) {
  //   return this.cacheWrap(this._checkupQuestions(checkup_id), `checkupQuestions_${checkup_id}`);
  // }

  availableAptitudeTypes() {
    return this.authManager(({ access_token }) => this.http.get<model.AryAvailableAptitudeTypes>(
      ep('v1/APPVS/APTITUDE_TYPES/GET_AVAILABLE_APTITUDE_TYPES'), {
      params: { access_token }, headers: { '_cache_key_': `avaliableAptitudeTypes`}
    }
    )).pipe(
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      this.validator(model.schemaAryAvailableAptitudeTypes, 'GET_AVAILABLE_APTITUDE_TYPES'),
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[`${b.aptitude_id}`] = b.aptitude_name; return a },
        {} as { [k: string]: string }
      ) : {})
    );
  }
  // public availableAptitudeTypes() {
  //   return this.cacheWrap(this._availableAptiudeTypes(), 'availableAptitudeTypes');
  // }

  workerAssessments(company_id: string, contact_id: string) {
    return this.authManager(({ access_token }) => this.http.get<{ assessment_id: number }[]>(
      ep('v1/APPPT/ASSESSMENTS/GET_WORKER_ASSESSMENTS'), {
      params: { access_token, company_id, contact_id }, 
      headers: { '_cache_key_': `workerAssessments_${company_id}_${contact_id}`}
    }
    ))
  }
  // public workerAssessments(company_id: string, contact_id: string) {
  //   return this.cacheWrap(
  //     this._workerAssessments(company_id, contact_id),
  //     `workerAssessments_${company_id}_${contact_id}`
  //   );
  // }

  machinesStatus() {
    return this.authManager(({ access_token }) => this.http.get<model.AryMachinesStatus>(
      ep('v1/APPAD/COMPANY_MACHINES/GET_MACHINES_STATUS'), {
      params: { access_token }, headers: { '_cache_key_': `machinesStatus`}
    }
    )).pipe(
      this.validator(model.schemaAryMachinesStatus, 'GET_MACHINES_STATUS'),
      //tap(e => console.log('API DEBUG machinesStatus', e)),
      map(resp => resp.reduce((a, b) => {
        a[b.status_id] = b.status_name;
        return a;
      }, {} as { [k: string]: string }))
    );
  }
  // public machinesStatus() {
  //   return this.cacheWrap(this._machinesStatus(), `machinesStatus`);
  // }

  machineImage(company_machine_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.ImageBlob>(
      ep('v1/APPAD/COMPANY_MACHINES/GET_MACHINE_IMAGE'), {
      params: { access_token, company_machine_id }, 
      headers: { '_cache_key_': `machineImage_${company_machine_id}`}
    }
    )).pipe(
      this.validator(model.schemaImageBlob, 'GET_MACHINE_IMAGE'),
    );
  }
  // public machineImage(company_machine_id: number) {
  //   return this.cacheWrap(this._machineImage(company_machine_id), `machineImage_${company_machine_id}`);
  // }

  // obté els EPI dels treballadors
  ipeWorkers(company_id: string, contact_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryIpeWorkers>(
      ep('v1/APPAD/COMPANY_PROTECTIONS/GET_IPE_WORKERS'), {
      params: { access_token, company_id, contact_id }, 
      headers: { '_cache_key_': `ipeWorkers_${company_id}_${contact_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryIpeWorkers, 'GET_IPE_WORKERS'),
    );
  }
  // public ipeWorkers(company_id: string, contact_id: string) {
  //   return this.cacheWrap(
  //     this._ipeWorkers(company_id, contact_id),
  //     `ipeWorkers_${company_id}_${contact_id}`
  //   )
  // }

  //v1/APPAD/COMPANY_PROTECTIONS/GET_IPE_JOBS
  ipeJobs(company_id:string, company_job_id: number | string) {
    return this.authManager(({ access_token })=>this.http.get<{
        company_proteccions_id: number,
        protections_id: number,
        company_protections_name: string,
        company_protections_description: string,
        company_protections_type: 'IND' | 'COL',
        company_protections_required: 0 | 1,
        body_parts_id: number,
        company_protections_normative: string // "UNE EN 374-5"
    }[]>(
      ep('v1/APPAD/COMPANY_PROTECTIONS/GET_IPE_JOBS'), {
        params: { access_token, company_id, company_job_id}
      }
    ));
  }

  ipeInfo(company_protections_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.IpeInfo>(
      ep('v1/APPAD/COMPANY_PROTECTIONS/GET_IPE_INFO'), {
      params: { access_token, company_protections_id }, 
      headers: { '_cache_key_': `ipeInfo_${company_protections_id}`}
    }
    )).pipe(
      map((info) => info),
      this.validator(model.schemaIpeInfo, 'GET_IPE_INFO')
    );
  }
  // public ipeInfo(company_proteccions_id: number) {
  //   return this.cacheWrap(this._ipeInfo(company_proteccions_id), `ipeInfo_${company_proteccions_id}`);
  // }

  ipeTypes() {
    return this.authManager(({ access_token }) => this.http.get<model.AryIpeTypes>(
      ep('v1/APPAD/COMPANY_PROTECTIONS/GET_IPE_TYPE_TRANSLATION'), {
      params: { access_token }, headers: { '_cache_key_': `ipeTypes`}
    }
    )).pipe(
      this.validator(model.schemaAryIpeTypes, 'GET_IPE_TYPE_TRANSLATION'),
      map(lst => lst.reduce((a, b) => {
        a[b.company_protections_type] = b.company_protections_type_translation;
        return a;
      }, {} as { [k: string]: string }))
    )
  }
  // public ipeTypes() {
  //   return this.cacheWrap(this._ipeTypes(), 'ipeTypes');
  // }

  bodyParts(body_parts_id: number) {
    return this.authManager(({ access_token }) => this.http.get<model.AryBodyParts>(
      ep('v1/APPPT/BODY_PARTS/GET_BODY_PARTS'), {
      params: { access_token, body_parts_id }, headers: { '_cache_key_': `bodyParts_${body_parts_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryBodyParts, 'GET_BODY_PARTS'),
      map((parts)=>{
        if (!parts.length) return ''
        const [{body_parts_name}] = parts;
        return body_parts_name;
      })
    )
  }
  // public bodyParts(body_parts_id: number) {
  //   return this.cacheWrap(this._bodyParts(body_parts_id), `bodyParts_${body_parts_id}`);
  // }
  
  availableIPEDeliveryStatus() {
    return this.authManager(({access_token})=>this.http.get<
      Record<model.IPEDelivery, {status_id:model.IPEDelivery, status_name:string, color:string }>
    >(
      ep('v1/APPAD/COMPANY_PROTECTIONS/GET_AVAILABLE_IPE_DELIVERIES_STATUS'), {
        params: { access_token }, headers: { '_cache_key_': `availableIPEDeliveryStatus`}
      }
    ))
  }

  getIPEDeliveryStatus(company_id:string, contact_id:string) {
    return this.authManager(({ access_token })=>this.http.get<
      Record<model.IPEDelivery, {status_id:model.IPEDelivery, status_name:string, color:string }>
    >(
      ep('v1/APPAD/COMPANY_PROTECTIONS/GET_IPE_DELIVERIES_STATUS'), {
        params: { access_token, company_id, contact_id }, 
        headers: { '_cache_key_': `getIPEDeliveryStatus_${company_id}_${contact_id}`}
      }
    ));
  }

  getAvailableIPEs(company_id:string) {
    return this.authManager(({access_token})=>this.http.get<model.IpeInfo[]>(
      ep('v1/APPAD/COMPANY_PROTECTIONS/GET_IPE_COMPANY'), {
        params:{ access_token, company_id}
      }
    ))
  }

  postCreateIPEWorker(company_job_id:number|string, name: string, description:string='', type: 'IND' | 'COL'='IND', requirement: 0|1 = 1 ) {
    return this.authManager(({access_token})=>this.http.post<{company_proteccions_id: number}>(
      ep('v1/APPAD/COMPANY_PROTECTIONS/POST_IPE_JOB'), {}, {
        params: { access_token, company_job_id, name, description, type, requirement }
      } 
    )).pipe(map(({company_proteccions_id})=>company_proteccions_id));
  }
  postIPEWorker(company_job_id:number|string, company_proteccions_id: number ) {
    return this.authManager(({access_token})=>this.http.post<{company_proteccions_id: number}>(
      ep('v1/APPAD/COMPANY_PROTECTIONS/POST_IPE_JOB'), {}, {
        params: { access_token, company_job_id, company_proteccions_id }
      } 
    )).pipe(map(({company_proteccions_id})=>company_proteccions_id));
  }

  deliverIPes(company_worker_id:string|number, payload:{[company_protections_id:string]:string}, delDate?:string, signature?:File ) {
    const ipe_useful_life = JSON.stringify(payload);
    const delivery_date = delDate || new Date().toISOString().split('.')[0];
    const body = new FormData();
    if (!!signature) {
      const readAsDataURL = bindCallback((file: File, cb: (r: string) => void) => {
        const reader = new FileReader();
        reader.onload = () => cb(reader.result as string);
        reader.readAsDataURL(file);
      });
      return readAsDataURL(signature).pipe(
        switchMap((dataURL)=>{
          const [_, b64] = dataURL.split(',');
          body.append('file', b64);
          body.append('file_name', signature.name);
          body.append('MIME', signature.type);
          return this.authManager(({access_token})=>this.http.post(
            ep('v1/APPAD/COMPANY_PROTECTIONS_WORKERS_DELIVERIES/POST_PROTECTION_DELIVERY_DATA'), body, {
              params: { access_token, company_worker_id, ipe_useful_life, delivery_date }
            }
          ));
        }),
      );
    } else return this.authManager(({access_token})=>this.http.post(
      ep('v1/APPAD/COMPANY_PROTECTIONS_WORKERS_DELIVERIES/POST_PROTECTION_DELIVERY_DATA'), body, {
        params: { access_token, company_worker_id, ipe_useful_life, delivery_date }
      }
    ))
  }

  // Nota: Alternativament, es podria passar {company_proctections_id:number, company_worker_id:number} com a paràmetres
  ipeDeliveryDocument(company_protections_workers_deliveries_id: number|string) {
    return this.authManager(({access_token})=>this.http.get<model.FileBlobResponse[]>(
      ep('v1/APPAD/COMPANY_PROTECTIONS/GET_IPE_WORKERS_DELIVERY_DOCUMENT'), {
        params: { access_token, company_protections_workers_deliveries_id }
      }
    ))
  }

  ipeDeliverySigned(company_protections_workers_deliveries_id:number) {
    return this.authManager(({access_token})=>this.http.post(
      ep('v1/APPAD/COMPANY_PROTECTIONS_WORKERS_DELIVERIES/POST_PROTECTION_DELIVERY_SIGNED'), undefined, {
        params: { access_token, company_protections_workers_deliveries_id }
      }
    ))
  }

  signIPEDelivery(company_protections_workers_deliveries_id:number, company_id:string) {
    const return_url = `${environment.webhost}/${company_id}/dashboard?doc=${company_protections_workers_deliveries_id}`
    return this.authManager(({access_token})=>this.http.post<{url:string}>(
      ep('v1/APPAD/COMPANY_PROTECTIONS_WORKERS_DELIVERIES/POST_SIGN_PROTECTION_DELIVERY'), undefined, {
        params: { access_token, company_protections_workers_deliveries_id, return_url }
      }
    ))
  }
  // auxiliar per refrescar alguns valors en el moment oportú
  signIPEDeliveryCallbackCalled = new Subject<void>();
  signIPEDeliveryCallbackCalled$ = this.signIPEDeliveryCallbackCalled.asObservable();


  identifierTypes() {
    return this.authManager(({ access_token }) => this.http.get<model.AryIdentifierTypes>(
      ep('v1/APP/IDENTIFIERS/GET_IDENTIFIER_TYPES'), {
      params: { access_token }, headers: { '_cache_key_': `identifierTypes`}
    }
    )).pipe(
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      this.validator(model.schemaAryIdentifierTypes, 'GET_IDENTIFIER_TYPES'),
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[`${b.identifier_type_id}`] = b.identifier_type_name; return a },
        {} as { [k: string]: string }
      ) : {})
    );
  }
  // public identifierTypes() {
  //   return this.cacheWrap(this._identifierTypes(), 'identifierTypes');
  // }

  sexIds() {
    return this.authManager(({ access_token }) => this.http.get<model.ArySexIds>(
      ep('v1/APP/SEXES/GET_SEXES'), {
      params: { access_token }, headers: { '_cache_key_': `sexIds`}
    }
    )).pipe(
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      this.validator(model.schemaArySexIds, 'GET_SEXES'),
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[`${b.sex_id}`] = b.sex_name; return a },
        {} as { [k: string]: string }
      ) : {})
    )
  }
  // public sexIds() {
  //   return this.cacheWrap(this._sexIds(), 'sexIds');
  // }

  // Properes revisions programades per a l'usuari (access_token) a l'empresa company_id
  upcomingCheckups(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryUpcomingCheckups>(
      ep('v1/APPVS/CHECKUPS/GET_UPCOMING_CHECKUPS'), {
      params: { access_token, company_id }, headers: { '_cache_key_': `upcomingCheckups_${company_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryUpcomingCheckups, 'GET_UPCOMING_CHECKUPS'),
    );
  }
  // public upcomingCheckups(company_id: string) {
  //   return this.cacheWrap(this._upcomingCheckups(company_id), `upcomingCheckups_${company_id}`);
  // }

  medicalCenters(company_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryMedicalCenters>(
      ep('v1/APPAD/WORKPLACES/GET_MEDICAL_CENTERS'), {
      params: { access_token, company_id }, headers: { '_cache_key_': `medicalCenters_${company_id}`}
    }
    )).pipe(
      this.validator(model.schemaAryMedicalCenters, 'GET_MEDICAL_CENTERS'),
    );
  }
  // public medicalCenters(company_id: string) {
  //   return this.cacheWrap(this._medicalCenters(company_id), `medicalCenters_${company_id}`);
  // }

  // available hours no farà servir la caché
  // NOTA: el format de les dates es 'd-m-Y'!
  medicalCenterAvailavility(company_id:string, medical_center_id:number, date_start:string, date_end:string) {
    return this.authManager(({ access_token })=>this.http.get<{
        day:  string, // "24-07-2025",
        hours: string[], // "09:00:00",
        status_id: string // "DISPONIBLE"
    }[]>(ep('v1/APPAD/WORKPLACES/GET_MEDICAL_CENTER_AVAILABLE_HOURS'), {
        params: { access_token, company_id, medical_center_id, date_start, date_end }
    }))
  }

  medicalTests(company_id:string, company_contract_id:number, contacts_id:string) {
    return this.authManager(({access_token})=>this.http.get<{[contact_id:string]:{
        name: string, surname: string, second_surname: string,
        birth_date: string, sex_id: string,
        start_date: string, end_date: string | null,
        recent_incorporation: boolean,
        company_worker_id: number[],
        jobs: {[job_id:string]: {
          job_id: number,
          company_job_name: string
        }},
        tests:{
          questionary_id: number,
          questionary_version: number,
          questionary_title: string,
          price: number | null,
          mandatory: boolean,
          status_id: number | null,
          status_name: string | null
        }[],
        last_aptitude_date: string, // "DATETIME",
        last_management_date: string | null,
        contact_info: {
          email: string,
          phone: string | null
        }
    }}>(
      ep('v1/APPAD/COMPANY_CONTRACTS/GET_COMPANY_CONTRACT_WORKERS_TESTS'), {
        params: { access_token, company_id, company_contract_id, contacts_id }, 
        //headers: { '_cache_key_': `medicalTests_${company_id}_${company_contract_id}`}
      }
    ))
  }

  postRevisioMedica(company_id:string, company_contract_id:number, workers:Array<{
      contact_id: string, 
      tests: Array<{questionary_id: number, questionary_version: number}>,
      checkup_center: number, 
      company_worker_id: number[]
    }>) {
      
      const body = new FormData();
      workers.forEach(w=>body.append('workers[]', JSON.stringify(w)));

      return this.authManager(({access_token})=>this.http.post<{sent: string[], failed: string[]}>(
        ep('v1/APPAD/COMPANY_CONTRACTS/POST_COMPANY_CONTRACT_WORKERS_CHECKUP_REQUEST'), body, {
          params: { access_token, company_id, company_contract_id }
        }
      ))
    }

  workerContactMethods(company_id: string, contact_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryWorkerContactMethods>(
      ep('v1/APP/CONTACT_METHODS/GET_WORKER_CONTACT_METHODS'),
      { params: { access_token, company_id, contact_id }, 
      headers: { '_cache_key_': `workerContactMethods_${company_id}_${contact_id}`} }
    ).pipe(
      this.validator(model.schemaAryWorkerContactMethods, 'GET_WORKER_CONTACT_METHODS'),
    )
    )
  }

  workerData(company_id:string, contact_id: string) {
    return this.authManager(({access_token})=>this.http.get<{[contact_id:string]: {
      machines: model.AryWorkerMachines,
      courses: {[contact_id:string]: {
        [agreement_id:string]: model.IAgreement
      }},
      protections: Array<{
        company_worker_id: number,
        risk_source_type_id: number,
        risk_source_id: number,
        company_proteccions_id: number,
        sent: string | null,
        useful_life: string | null, // <-date
        company_proteccions_workers_deliveries_document_id: number | null,
        company_proteccions_workers_id: number | null,
        company_proteccions_workers_deliveries_id: number | null
      }>,
      checkups: model.AryCheckups
    }
  }>(
      ep('v1/APPAD/COMPANY_WORKERS/GET_WORKER_DATA'), {
        params: { access_token, company_id, contact_id },
        headers: { '_cache_key_': `workerData_${company_id}_${contact_id}`}
      }
    ));
  }

  // public workerContactMethods(company_id: string, contact_id: string) {
  //   return this.cacheWrap(
  //     this._getWorkerContactMethods(company_id, contact_id),
  //     `workerContactMethods_${company_id}_${contact_id}`
  //   );
  // }

  zipCodeData(zip_code: string) {
    return this.authManager(({ access_token }) => this.http.get<model.AryZipCodeData>(
      ep('v1/APP/MAPS_ZIP_CODE/GET_ZIP_CODE_DATA'),
      { params: { access_token, zip_code }, 
      headers: { '_cache_key_': `zipCode_${zip_code}` } }
    )).pipe(
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      this.validator(model.schemaAryZipCodeData, 'GET_ZIP_CODE_DATA'),
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => {
          a[`${b.zip_code}`] = b;
          return a;
        }, {} as { [k: string]: { town_id: number, region_id: number, province_id: number, autonomous_community_id: number, country_id: number, zip_code_id: number, IBAN_code: string } }
      ) : {})
    );
  }

  // public zipCodeData(zipCode: string) {
  //   return this.cacheWrap(this._getZipCodeData(zipCode), `zipCode__${zipCode}`);
  // }

  //Esta petición nos permite insertar un trabajador.
  postTreballador(company_id: string, wdata: {
    identifier_type_id: string,
    contact_id: string,
    name: string,
    surname: string,
    second_surname?: string,
    birthday: string,
    sex_id: string,
    language_id: string,
    password: string,
    workplace_id?: string,
    section_id?: string,
    tenure: string,
    company_job_id: string,
    contact_methods?: any[]
  }) {
    const fdata = new FormData();

    // Afegim cada contact_method al body com a JSON (sense id)
    if (Array.isArray(wdata.contact_methods)) {
      wdata.contact_methods.forEach((cm: any) => {
        // Elimina l'id si existeix (no s'ha d'enviar en alta)
        const { contact_method_id, ...cmNoId } = cm;
        fdata.append("contact_methods[]", JSON.stringify(cmNoId));
      });
    }

    // Elimina contact_methods del wdata per no passar-lo com a param
    delete wdata.contact_methods;

    // Elimina camps buits o nuls opcionals
    Object.keys(wdata).forEach(key => {
      if (
        wdata[key as keyof typeof wdata] === undefined ||
        wdata[key as keyof typeof wdata] === ''
      ) {
        delete wdata[key as keyof typeof wdata];
      }
    });

    // company_id és obligatori i va com a param
    const params = { company_id, ...wdata };

    return this.authManager(({ access_token }) => this.http.post<{}>(
      ep('v1/APPAD/COMPANY_WORKERS/POST_WORKER_ALTA'),
      fdata,
      { params: { access_token, ...params } }
    )).pipe(this.uify());
  }

  // esborra / elimina treballador (no cal caché)
  baixaTreballador(company_id: string, contact_id: string) {
    return this.authManager(({ access_token }) => this.http.post<any[]>(
      ep('v1/APPAD/COMPANY_WORKERS/POST_WORKER_BAIXA'), {}, {
      params: { access_token, company_id, contact_id }
    }
    )).pipe(this.uify())
  }

  consentimentInformat(checkup_id: string | number) {
    return this.authManager(({access_token})=>this.http.get<{
        rgpdText: string,
        covidSymptoms: string | null,
        closeContact: string | null,
        onlineReport: string,
        biometricData: string,
        advertisingServices: string,
        electronicAdvertising: string
    }[]>(ep('v1/APPVS/CHECKUP_CONSENTIMENTS/GET_CHECKUP_CONSENT_CONTENT'),{
      params: { checkup_id, access_token }
    }))
  }


  /*
  Nota: els que acaben en VALUES retornen els valors possibles i el seu significat.
  Algunes preguntes poden no tenir valors predefinits, ja que són camps de text que el
  metge introdueix manualment.
  Nota2: cal revisar les rutes, algunes estan malament. On diu CHECKUPS hauria de ser CHECKUP_QUESTIONARY

  /API/v1/APPVS/CHECKUPS/GET_CHECKUP_QUESTIONARY_VISION
  /API/v1/APPVS/CHECKUPS/GET_CHECKUP_QUESTIONARY_VISION_VALUES

  /API/v1/APPVS/CHECKUPS/GET_CHECKUP_QUESTIONARY_HEARING
  /API/v1/APPVS/CHECKUPS/GET_CHECKUP_QUESTIONARY_HEARING_VALUES

  /API/v1/APPVS/CHECKUPS/GET_CHECKUP_QUESTIONARY_ELECTROCARDIOGRAM
  /API/v1/APPVS/CHECKUPS/GET_CHECKUP_QUESTIONARY_ELECTROCARDIOGRAM_VALUES
  /API/v1/APPVS/CHECKUPS/GET_CHECKUP_ANALYTICS_DOCUMENT

  /API/v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS_BLOOD_PRESSURE
  /API/v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS_BLOOD_PRESSURE_VALUES

  /API/v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS_IMC
  /API/v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS_IMC_VALUES

  /API/v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONARY_ANALYTICS
  /API/v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONARY_ANALYTICS_VALUES
  */

  salutHearing(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.ArySalutCheckup>(
      ep('v1/APPVS/CHECKUP_QUESTIONARY/GET_CHECKUP_QUESTIONARY_HEARING'), {
      params: { access_token, checkup_id }, 
      headers: { '_cache_key_': `salutHearing_${checkup_id}` }
    }).pipe(
      this.validator(model.schemaArySalutCheckup, 'GET_CHECKUP_QUESTIONARY_HEARING')
    ));
  }
  // public salutHearing(checkup_id: string) {
  //   return this.cacheWrap(this._salutHearing(checkup_id), `salutHearing_${checkup_id}`);
  // }

  salutHearingValues() {
    return this.authManager(({ access_token }) => this.http.get<any[]>(
      ep('v1/APPVS/CHECKUP_QUESTIONARY/GET_CHECKUP_QUESTIONARY_HEARING_VALUES'), {
      params: { access_token }, 
      headers: { '_cache_key_': `salutHearingValues` } }
    ));
  }
  // public salutHearingValues() {
  //   return this.cacheWrap(this._salutHearingValues(), `salutHearingValues`);
  // }

  /*
    // Properes revisions programades per a l'usuari (access_token) a l'empresa company_id
    private _upcomingCheckups(company_id: string) {
      return this.authManager(({access_token})=>this.http.get<{
        checkup_id: number,
        checkup_workplace_id: number,
        mark_time_agenda: string, // "2025-12-09 07:30:00",
        workplace_zip_code_id: number,
        workplace_town_id: number,
        workplace_region_id: number,
        workplace_province_id: number,
        workplace_autonomous_community_id: number,
        workplace_country_id: number
      }[]>(
        ep('v1/APPVS/CHECKUPS/GET_UPCOMING_CHECKUPS'), {
          params: { access_token, company_id }
        }
      ))
    }
    public upcomingCheckups(company_id: string) {
      return this.cacheWrap(this._upcomingCheckups(company_id), `upcomingCheckups_${company_id}`);
    }

      private _checkupQuestions(checkup_id: string) {
    return this.authManager(({access_token})=>this.http.get<{
      checkup_question_id: number,
      checkup_question_value: string,
      question_name: string,
      question_input_format_id: string,
      area_questionnair_id: number,
      area_questionnair_name: string,
      area_questionnair_block_id: number,
      area_questionnair_block_name: string,
      order: number,
      values: {
        value_name: string,
        value: string
      }[]
    }[]>(
      ep('v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS'), {
        params: { access_token , checkup_id}
      }
    ));
  }

  public checkupQuestions(checkup_id: string) {
    return this.cacheWrap(this._checkupQuestions(checkup_id), `checkupQuestions_${checkup_id}`);
  }
  */

  // Obtenir informació reconeixement visió
  salutVision(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.ArySalutCheckup>(
      ep('v1/APPVS/CHECKUP_QUESTIONARY/GET_CHECKUP_QUESTIONARY_VISION?'), {
      params: { access_token, checkup_id }, 
      headers: { '_cache_key_': `salutVision_${checkup_id}`
    }}
    )).pipe(
      this.validator(model.schemaArySalutCheckup, 'GET_CHECKUP_QUESTIONARY_VISION')
    );
  }

  // public salutVision(checkup_id: string) {
  //   return this.cacheWrap(this._salutVision(checkup_id), `salutVision_${checkup_id}`);
  // }

  salutVisionValues() {
    return this.authManager(({ access_token }) => this.http.get<any[]>(
      ep('v1/APPVS/CHECKUP_QUESTIONARY/GET_CHECKUP_QUESTIONARY_VISION_VALUES'), {
      params: { access_token }, 
      headers: { '_cache_key_': `salutVisionValues`
    }}
    ));
  }

  // public salutVisionValues() {
  //   return this.cacheWrap(this._salutVisionValues(), 'salutVisionValues');
  // }

  salutElectrocardiogram(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.ArySalutCheckup>(
      ep('v1/APPVS/CHECKUP_QUESTIONARY/GET_CHECKUP_QUESTIONARY_ELECTROCARDIOGRAM'), {
      params: { access_token, checkup_id }, 
      headers: { '_cache_key_': `salutElectrocardiogram_${checkup_id}`
    }}
    )).pipe(
      this.validator(model.schemaArySalutCheckup, 'GET_CHECKUP_QUESTIONARY_ELECTROCARDIOGRAM')
    );
  }

  // public salutElectrocardiogram(checkup_id: string) {
  //   return this.cacheWrap(this._salutElectrocardiogram(checkup_id), `salutElectrocardiogram_${checkup_id}`);
  // }

  salutElectrocardiogramValues() {
    return this.authManager(({ access_token }) => this.http.get<any[]>(
      ep('v1/APPVS/CHECKUP_QUESTIONARY/GET_CHECKUP_QUESTIONARY_ELECTROCARDIOGRAM_VALUES'), {
      params: { access_token }, 
      headers: { '_cache_key_': `salutElectrocardiogramValues`
    }}
    ));
  }

  // public salutElectrocardiogramValues() {
  //   return this.cacheWrap(this._salutElectrocardiogramValues(), 'salutElectrocardiogramValues');
  // }

  salutElectrocardiogramDocument(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.FileBlobResponse>(
      ep('v1/APPVS/CHECKUPS/GET_CHECKUP_ELECTROCARDIOGRAM_DOCUMENT'), {
      params: { access_token, checkup_id }, 
      headers: { '_cache_key_': `salutElectrocardiogramDocument_${checkup_id}`
    }}
    )).pipe(
      this.validator(model.schemaFileBlobResponse, 'GET_CHECKUP_ELECTROCARDIOGRAM_DOCUMENT')
    );
  }

  // public salutElectrocardiogramDocument(checkup_id: string) {
  //   return this.cacheWrap(this._salutElectrocardiogramDocument(checkup_id), `salutElectrocardiogramDocument_${checkup_id}`);
  // }

  salutAnalytics(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.ArySalutCheckup>(
      ep('v1/APPVS/CHECKUP_QUESTIONARY/GET_CHECKUP_QUESTIONARY_ANALYTICS'), {
      params: { access_token, checkup_id }, 
      headers: { '_cache_key_': `salutAnalytics_${checkup_id}`
    }}
    )).pipe(
      this.validator(model.schemaArySalutCheckup, 'GET_CHECKUP_QUESTIONARY_ANALYTICS')
    );
  }

  // public salutAnalytics(checkup_id: string) {
  //   return this.cacheWrap(this._salutAnalytics(checkup_id), `salutAnalytics_${checkup_id}`);
  // }

  salutAnalyticsValues() {
    return this.authManager(({ access_token }) => this.http.get<any[]>(
      ep('v1/APPVS/CHECKUP_QUESTIONARY/GET_CHECKUP_QUESTIONARY_ANALYTICS_VALUES'), {
      params: { access_token }, 
      headers: { '_cache_key_': `salutAnalyticsValues`}
    }
    ));
  }

  // public salutAnalyticsValues() {
  //   return this.cacheWrap(this._salutAnalyticsValues(), 'salutAnalyticsValues');
  // }

  // No cal caché
  salutAnalyticsDocument(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.FileBlobResponse>(
      ep('v1/APPVS/CHECKUPS/GET_CHECKUP_ANALYTICS_DOCUMENT'), {
      params: { access_token, checkup_id }
    }
    )).pipe(
      this.validator(model.schemaFileBlobResponse, 'GET_CHECKUP_ANALYTICS_DOCUMENT')
    );
  }

  // public salutAnalyticsDocument(checkup_id: string) {
  //   return this.cacheWrap(this._salutAnalyticsDocument(checkup_id), `salutAnalyticsDocument_${checkup_id}`);
  // }

  salutIMC(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.ArySalutCheckup>(
      ep('v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS_IMC'), {
      params: { access_token, checkup_id }, 
      headers: { '_cache_key_': `salutIMC_${checkup_id}`}
    }
    )).pipe(
      this.validator(model.schemaArySalutCheckup, 'GET_CHECKUP_QUESTIONS_IMC')
    );
  }

  // public salutIMC(checkup_id: string) {
  //   return this.cacheWrap(this._salutIMC(checkup_id), `salutIMC_${checkup_id}`);
  // }

  salutIMCValues() {
    return this.authManager(({ access_token }) => this.http.get<any[]>(
      ep('v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS_IMC_VALUES'), {
      params: { access_token }, 
      headers: { '_cache_key_': `salutIMCValues`}
    }
    ));
  }

  // public salutIMCValues() {
  //   return this.cacheWrap(this._salutIMCValues(), 'salutIMCValues');
  // }

  salutBloodPressure(checkup_id: string) {
    return this.authManager(({ access_token }) => this.http.get<model.ArySalutCheckup>(
      ep('v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS_BLOOD_PRESSURE'), {
      params: { access_token, checkup_id }, 
      headers: { '_cache_key_': `salutBloodPressure_${checkup_id}`}
    }
    )).pipe(
      this.validator(model.schemaArySalutCheckup, 'GET_CHECKUP_QUESTIONS_BLOOD_PRESSURE')
    );
  }

  // public salutBloodPressure(checkup_id: string) {
  //   return this.cacheWrap(this._salutBloodPressure(checkup_id), `salutBloodPressure_${checkup_id}`);
  // }

  salutBloodPressureValues() {
    return this.authManager(({ access_token }) => this.http.get<any[]>(
      ep('v1/APPVS/CHECKUP_QUESTIONS/GET_CHECKUP_QUESTIONS_BLOOD_PRESSURE_VALUE'), {
      params: { access_token }, 
      headers: { '_cache_key_': `salutBloodPressureValues`}
    }
    ));
  }

  // public salutBloodPressureValues() {
  //   return this.cacheWrap(this._salutBloodPressureValues(), 'salutBloodPressureValues');
  // }

  // no cal caché
  public getAptitudeCertificate(company_id:string, contact_id:string, checkup_id: number) {
    return this.authManager(({access_token})=>this.http.get<model.FileBlobResponse>(
      ep('v1/APPVS/CHECKUPS/GET_APTITUDE_CERTIFICATE'), {
        params: { access_token, company_id, contact_id, checkup_id }
      }
    )).pipe(
      this.validator(model.schemaFileBlobResponse, 'GET_APTITUDE_CERTIFICATE')
    );
  }

  // No cal caché
  public CAEDocument(company_id: string, contact_id: string) {
    return this.authManager(({ access_token }) => this.http.get<{ filename: string, b64file: string }>(
      ep('v1/APPVS/CHECKUPS/GET_CAE_DOCUMENT'), {
      params: { access_token, company_id, contact_id }
    }
    ));
  }

  postUpdateBirthDate(company_id:string, contact_id:string, birth_date: string) {
    return this.authManager(({access_token})=>this.http.post<any>(
      ep('v1/APP/USERS/POST_UPDATE_USER_BIRTH_DATE'), {}, {
        params: { access_token, company_id, contact_id, birth_date}
      }
    ));
  }

  //Esta petición nos permite actualizar el perfil del usuario.
  postUpdateUser(
    company_id: string,
    wdata: {
      contact_id: string,
      name?: string,
      surname?: string,
      second_surname?: string,
      birthday?: string,
      sex_id?: string,
      language_id?: string,
      password?: string,
      workplace_id?: string,
      section_id?: string,
      tenure?: string,
      company_job_id?: string,
      contact_methods?: any[]
    }
  ) {
    const fdata = new FormData();

    // Afegim cada contact_method al body com a JSON
    if (Array.isArray(wdata.contact_methods)) {
      wdata.contact_methods.forEach((cm: any) => {
        fdata.append("contact_methods[]", JSON.stringify(cm));
      });
    }

    // Elimina contact_methods del wdata per no passar-lo com a param
    delete wdata.contact_methods;

    // Elimina camps buits o nuls opcionals (excepte obligatoris)
    Object.keys(wdata).forEach(key => {
      if (
        (wdata[key as keyof typeof wdata] === undefined || wdata[key as keyof typeof wdata] === '') &&
        key !== 'contact_id'
      ) {
        delete wdata[key as keyof typeof wdata];
      }
    });

    // company_id i contact_id són obligatoris
    const { contact_id, ...params } = wdata;

    // Només passem params amb valors primitius (string, number, boolean)
    const filteredParams: Record<string, string | number | boolean> = {};
    Object.entries(params).forEach(([k, v]) => {
      if (
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean'
      ) {
        filteredParams[k] = v;
      }
    });

    return this.authManager(({ access_token }) => this.http.post(
      ep('v1/APPAD/COMPANY_WORKERS/PATCH_WORKER_UPDATE'),
      fdata,
      { // TODO: falta paràmetre 'advertising' 0|1
        params: { access_token, company_id, contact_id, ...filteredParams }
      }
    )).pipe(this.uify());
  }

  warnRisk(company_id: string, text: string, blobs:Blob[]) {
    const formData = new FormData();
    blobs.forEach(blob=>formData.append('attachments[]', blob));

    return this.authManager(({ access_token }) => this.http.post(
      ep('v1/APPAD/COMPANY_RISKS/POST_WARN_RISK'), formData, {
      params: { access_token, company_id, text }, // text - body del mail
      responseType:'text'
    }
    )).pipe(this.uify());
  }

  reportAccident(company_id: string, text: string, blobs:Blob[]) {
    const formData = new FormData();
    blobs.forEach(blob=>formData.append('attachments[]', blob));

    return this.authManager(({ access_token }) => this.http.post(
      ep('v1/APPAD/COMPANY_RISKS/POST_ACCIDENT'), formData, {
      params: { access_token, company_id, text }, // text - body del mail
      responseType: 'text'
    }
    )).pipe(this.uify());
  }

  postFBToken(firebase_token:string) {
    // Evito l'auth manager per problemes amb un error 401 misteriós
    return this._authCred$.pipe( take(1),
      switchMap(({access_token})=>this.http.post(
        ep('v1/APP/COMMUNICATIONS/POST_USER_NOTIFICATION_TOKEN'),{}, {
          params: { device_id: this._device_id.identifier, firebase_token, access_token },
          responseType:'text'
        }
      ))
    )
  }
  deleteFBToken() {
    return this.authManager(({access_token})=>this.http.delete(
      ep('v1/APP/COMMUNICATIONS/DELETE_USER_NOTIFICATION_TOKEN'), {
        params: { device_id:this._device_id.identifier, access_token },
        responseType:'text'
      }
    ))
  }

  postCheckupConsent(checkup_id: number | string, company_id:string, data:{
    contact_id: string,
    name: string,
    surname: string,
    second_surname: string | null,
    birth_date: string,
    sex_id: string,
    contact_methods: ICttMethod[],
    advertising: 0|1,
    online: 0|1,
    waiver:0|1,
    autonomous_community_id?: number,
    postal_code_id?: number,
    city_id?: number,
    province_id?: number,
    country_id?: number,
    address?: string,
    region_id?: number
  }) {
    let _params = {
        checkup_id, 
        name: data.name, surname:data.surname, second_surname: data.second_surname||'',
        birthday: data.birth_date, sex_id: data.sex_id, 
        online: data.online?1:0, waiver: data.waiver?1:0, advertising: data.advertising?1:0,
    }
    if (!!data.postal_code_id) {
      const { 
        autonomous_community_id, 
        postal_code_id, city_id, 
        province_id, country_id, 
        address, region_id,
      } = data;
      const _add_params = { 
        autonomous_community_id, 
        postal_code_id, city_id, 
        province_id, country_id, 
        address, region_id,
      };
      _params = { ..._params, ..._add_params};
    }
    const body = new FormData();
    data.contact_methods.forEach(
      ctt_meth=>body.append('contact_methods[]', JSON.stringify(ctt_meth))
    );
    return this.authManager(({ access_token })=>this.http.post(
      ep('v1/APPVS/CHECKUP_CONSENTIMENTS/POST_CHECKUP_CONSENTIMENTS'), body, {
      headers: { "Authorization": `Basic ${this._basicAuth}` },
      params: {
        ..._params,
        access_token
      },
      responseType:'text'
    }).pipe(
      switchMap(rsp=>{
        console.log('Dades enviades correctament', rsp);
        return this.http.post<{url:string}>(
          ep('v1/APPVS/CHECKUP_CONSENTIMENTS/POST_SIGN_CHECKUP_CONSENT'),{}, {
            params: {
              checkup_id, access_token,
              return_url:environment.webhost+`/${company_id}/tabs/salut/reconeixement/${checkup_id}/consentiment-done`
            }
          }
        )
      })
    ))
  }

  postConsentSignedFeedback(checkup_id:number|string) {
    return this.authManager(({ access_token })=>this.http.post(
      ep('v1/APPVS/CHECKUP_CONSENTIMENTS/POST_CHECKUP_CONSENT_SIGNED'),{},{
        params: { checkup_id, access_token }, responseType:'text'
      }
    ))
  }


  // answerableQuestions(checkup_id: number|string ) {
  //   return this.authManager(({access_token})=>this.http.get<any>(
  //     ep('v1/APPVS/CHECKUP_QUESTIONS/GET_ANSWERABLE_QUESTIONS'), {
  //       params: { checkup_id, access_token }
  //     }
  //   ))
  // }
  answerQuestion(checkup_id: number|string, checkup_question_id: number|string, value: string ) {
    return this.authManager(({access_token})=>this.http.post<any>(
      ep('v1/APPVS/CHECKUP_QUESTIONS/POST_ANSWERABLE_QUESTIONS'), {}, {
        params: { checkup_id, checkup_question_id, value, access_token }
      }
    )).pipe(
      map((resp:any)=>Object.values(resp) as model.AryCheckupQuestions),
    )
  }

  accountCreation() {
    return this.http.get<{can_create: boolean}>(`${environment.webhost}/assets/create_account.json`).pipe(
      //tap(nxt=>console.log('account creation', nxt), err=>console.log('account creation error', err)),
      map(resp=>!!resp),
      catchError((err)=>of(false))
    )
  }
}


